/**
 * Last-used colours for Harbour cards. Quiet thresholds match pulse
 * (owner last seen 30 days, orders 14). Frontend never invents why-here.
 *
 * Radar = owner last seen + last order. Ladder = adoption feature ticks.
 */

import dayjs from 'dayjs'
import {
  HARBOUR_ADOPTION_LADDER_TIER,
  HARBOUR_ADOPTION_TICKS,
  HARBOUR_CARD_LAST_USED_LIMIT,
  HARBOUR_CARD_RADAR_FEATURES,
  HARBOUR_LAST_USED_FEATURE,
  HARBOUR_LAST_USED_STATUS,
  HARBOUR_LAST_USED_TONE,
  getHarbourLastUsedFeatureLabel,
  getHarbourLastUsedQuietDays,
} from 'src/constants/harbour'

export function getHarbourLastUsedDays(at, now = new Date()) {
  if (!at) {
    return null
  }
  const then = dayjs(at)
  const reference = dayjs(now)
  if (!then.isValid() || !reference.isValid()) {
    return null
  }
  return reference.startOf('day').diff(then.startOf('day'), 'day')
}

export function getHarbourLastUsedTone(
  entry = {},
  { feature, now = new Date() } = {},
) {
  const status = entry.status || HARBOUR_LAST_USED_STATUS.NEVER
  if (status === HARBOUR_LAST_USED_STATUS.UNKNOWN) {
    return HARBOUR_LAST_USED_TONE.UNKNOWN
  }
  if (status === HARBOUR_LAST_USED_STATUS.ON) {
    return entry.expired
      ? HARBOUR_LAST_USED_TONE.QUIET
      : HARBOUR_LAST_USED_TONE.FRESH
  }
  if (status !== HARBOUR_LAST_USED_STATUS.DATE || !entry.at) {
    return HARBOUR_LAST_USED_TONE.NEVER
  }
  const days = getHarbourLastUsedDays(entry.at, now)
  if (days == null) {
    return HARBOUR_LAST_USED_TONE.UNKNOWN
  }
  const quietAfter = getHarbourLastUsedQuietDays(feature)
  return days >= quietAfter
    ? HARBOUR_LAST_USED_TONE.QUIET
    : HARBOUR_LAST_USED_TONE.FRESH
}

export function formatHarbourLastUsedText(
  entry = {},
  now = new Date(),
  feature,
) {
  const status = entry.status || HARBOUR_LAST_USED_STATUS.NEVER
  if (status === HARBOUR_LAST_USED_STATUS.UNKNOWN) {
    return 'Unknown'
  }
  if (status === HARBOUR_LAST_USED_STATUS.ON) {
    if (feature === HARBOUR_LAST_USED_FEATURE.XERO) {
      return entry.expired ? 'Expired' : 'Connected'
    }
    if (feature === HARBOUR_LAST_USED_FEATURE.RESTRICTED) {
      const n = Number(entry.count) || 0
      return n === 1 ? '1 on' : `${n} on`
    }
    return 'On'
  }
  if (status !== HARBOUR_LAST_USED_STATUS.DATE || !entry.at) {
    return 'Never'
  }
  const days = getHarbourLastUsedDays(entry.at, now)
  if (days == null) {
    return 'Unknown'
  }
  if (days <= 0) {
    return 'Today'
  }
  if (days === 1) {
    return 'Yesterday'
  }
  return `${days} days ago`
}

function lastUsedMap(card = {}) {
  return card.lastUsed && typeof card.lastUsed === 'object' ? card.lastUsed : {}
}

function chipFromEntry(feature, entry = {}, now) {
  return {
    key: feature,
    label: entry.label || getHarbourLastUsedFeatureLabel(feature),
    text: formatHarbourLastUsedText(entry, now, feature),
    tone: getHarbourLastUsedTone(entry, { feature, now }),
  }
}

/**
 * Health radar: last login and last order, always visible.
 */
export function getHarbourCardRadar(card = {}, now = new Date()) {
  const lastUsed = lastUsedMap(card)
  return HARBOUR_CARD_RADAR_FEATURES.map(feature =>
    chipFromEntry(feature, lastUsed[feature] || {}, now),
  )
}

/**
 * Adoption ladder strip — critical rungs first (receipting, recipes, GP),
 * then secondary ticks (invoices, stock, Xero, …). Login and orders stay on
 * the radar. Each chip includes tier for card layout.
 */
export function getHarbourCardLadder(card = {}, now = new Date()) {
  const lastUsed = lastUsedMap(card)
  const ticksByKey = new Map(
    (Array.isArray(card.adoptionTicks) ? card.adoptionTicks : []).map(tick => [
      tick.key,
      tick,
    ]),
  )
  const chips = []
  for (const meta of HARBOUR_ADOPTION_TICKS) {
    const tick = ticksByKey.get(meta.key)
    const fromLastUsed = lastUsed[meta.feature] || {}
    const entry = {
      status:
        tick?.status || fromLastUsed.status || HARBOUR_LAST_USED_STATUS.NEVER,
      at: tick?.at || fromLastUsed.at || null,
      count: fromLastUsed.count,
      expired: fromLastUsed.expired,
      label: tick?.label || meta.label,
    }
    chips.push({
      ...chipFromEntry(meta.feature, entry, now),
      key: meta.key,
      label: tick?.label || meta.label,
      tier: meta.tier || HARBOUR_ADOPTION_LADDER_TIER.SECONDARY,
    })
    if (chips.length >= HARBOUR_CARD_LAST_USED_LIMIT) {
      break
    }
  }
  return chips
}

export function splitHarbourCardLadder(ladder = []) {
  const rows = Array.isArray(ladder) ? ladder : []
  return {
    critical: rows.filter(
      tick => tick.tier !== HARBOUR_ADOPTION_LADDER_TIER.SECONDARY,
    ),
    secondary: rows.filter(
      tick => tick.tier === HARBOUR_ADOPTION_LADDER_TIER.SECONDARY,
    ),
  }
}
